// =============================================================================
// FILE: packages/renderer/src/lib/standardsAuditor.ts
// PURPOSE: "Global Standards Auditor" + "Cable Test Validator". Deterministic,
//   offline compliance engine that runs a code pass (EU / NEC / CEC / TIA) over
//   the project's design metrics — cable-tray fill, TIA-568 channel length
//   (90m/100ft rule), TIA-607 bonding, NEC 800.110 firestop, EN-50174
//   separation — and validates uploaded cable-test results against the limits.
// =============================================================================

export type Region = 'US_NEC' | 'CA_CEC' | 'EU' | 'INTL_TIA';

export const REGIONS: Record<Region, { label: string; body: string }> = {
  US_NEC:   { label: 'United States / Canada (NEC/CEC · TIA/BICSI)',  body: 'NFPA 70 (NEC) / C22.1 (CEC) + ANSI/TIA-568/607' },
  CA_CEC:   { label: 'Canada (CEC · TIA)',              body: 'CSA C22.1 (CEC) + ANSI/TIA-568' },
  EU:       { label: 'European Union (EN · ISO/IEC)',    body: 'EN 50173 / EN 50174 + ISO/IEC 11801' },
  INTL_TIA: { label: 'International (ISO/IEC · TIA)',     body: 'ISO/IEC 11801 + ANSI/TIA-568' },
};

export type CheckStatus = 'pass' | 'violation' | 'warning';

export interface AuditCheck {
  id: string;
  code: string;        // e.g. "NEC 800.110"
  title: string;
  status: CheckStatus;
  detail: string;
  measured?: string;
  limit?: string;
  exception?: string;  // set when a Technical Exception is granted
}

export interface AuditInputs {
  region: Region;
  longestRunFt: number;   // longest horizontal permanent-link run (ft)
  trayFillPct: number;    // cable-tray fill ratio (%)
  hasTmgb: boolean;       // TIA-607 telecom main grounding busbar present
  firestopConfirmed: boolean; // NEC 800.110 penetration firestop
  separationMm: number;   // power/data separation (mm) — EN 50174
}

export interface AuditResult {
  region: Region;
  checks: AuditCheck[];
  violations: number;
  warnings: number;
  passed: number;
  compliant: boolean; // no outstanding violations (exceptions count as resolved)
}

const FT_PER_M = 3.28084;

export function runAudit(i: AuditInputs): AuditResult {
  const checks: AuditCheck[] = [];
  const runM = i.longestRunFt / FT_PER_M;

  // TIA-568 / ISO-11801 — 90 m permanent link (100 m channel)
  checks.push({
    id: 'chk-length',
    code: i.region === 'EU' ? 'ISO/IEC 11801 §6' : 'ANSI/TIA-568.1-D §6.3',
    title: 'Horizontal permanent-link length (90 m rule)',
    status: runM > 90 ? 'violation' : runM > 85 ? 'warning' : 'pass',
    detail: runM > 90
      ? 'Longest run exceeds the 90 m permanent-link limit — reroute or add an intermediate TR.'
      : runM > 85 ? 'Run is within 5 m of the limit — verify slack allowance.' : 'All horizontal runs within limit.',
    measured: `${i.longestRunFt} ft (${runM.toFixed(1)} m)`,
    limit: '90 m (295 ft) permanent link',
  });

  // Cable-tray fill
  const fillLimit = i.region === 'US_NEC' || i.region === 'CA_CEC' ? 50 : 40;
  const fillCode = i.region === 'CA_CEC' ? 'CEC Rule 12-2200' : i.region === 'EU' ? 'EN 50174-2 §5' : 'NEC 392.22';
  checks.push({
    id: 'chk-tray',
    code: fillCode,
    title: 'Cable-tray fill ratio',
    status: i.trayFillPct > fillLimit ? 'violation' : i.trayFillPct > fillLimit - 5 ? 'warning' : 'pass',
    detail: i.trayFillPct > fillLimit
      ? `Fill exceeds the ${fillLimit}% maximum — upsize the tray or add a parallel run.`
      : 'Tray fill within the allowable maximum.',
    measured: `${i.trayFillPct}%`,
    limit: `${fillLimit}% max`,
  });

  // TIA-607 grounding/bonding
  checks.push({
    id: 'chk-ground',
    code: i.region === 'EU' ? 'EN 50310' : 'ANSI/TIA-607-D',
    title: 'Telecom bonding & grounding (TMGB)',
    status: i.hasTmgb ? 'pass' : 'violation',
    detail: i.hasTmgb ? 'TMGB present; bonding backbone verified.' : 'No TMGB in the design — add the telecom main grounding busbar and bonding conductor.',
  });

  // NEC 800.110 firestop / raceway
  checks.push({
    id: 'chk-firestop',
    code: i.region === 'CA_CEC' ? 'CEC 2-128' : 'NEC 800.110',
    title: 'Penetration firestop / raceway integrity',
    status: i.firestopConfirmed ? 'pass' : 'warning',
    detail: i.firestopConfirmed ? 'Rated penetrations firestopped per detail.' : 'Firestop details not confirmed on all rated penetrations — verify before close-out.',
  });

  // EN 50174 separation — EU only (informational elsewhere)
  const sepReq = 200; // mm typical unshielded parallel run
  checks.push({
    id: 'chk-separation',
    code: 'EN 50174-2 §6.4',
    title: 'Power / data separation distance',
    status: i.region === 'EU' ? (i.separationMm >= sepReq ? 'pass' : 'violation') : (i.separationMm >= sepReq ? 'pass' : 'warning'),
    detail: i.separationMm >= sepReq
      ? 'Separation meets EN 50174 guidance.'
      : `Separation below ${sepReq} mm — increase spacing or use a divider/shielded pathway.`,
    measured: `${i.separationMm} mm`,
    limit: `${sepReq} mm min`,
  });

  const outstanding = checks.filter((c) => c.status === 'violation' && !c.exception);
  return {
    region: i.region,
    checks,
    violations: checks.filter((c) => c.status === 'violation').length,
    warnings: checks.filter((c) => c.status === 'warning').length,
    passed: checks.filter((c) => c.status === 'pass').length,
    compliant: outstanding.length === 0,
  };
}

/** Auto-draft a Technical Justification (RFE) for a specific violation. */
export function draftException(check: AuditCheck, region: Region): string {
  return `TECHNICAL JUSTIFICATION — Request for Exception (RFE)\n` +
    `Code reference: ${check.code} (${REGIONS[region].body}).\n` +
    `Field condition: ${check.title} measured ${check.measured ?? 'n/a'} against a limit of ${check.limit ?? 'n/a'}.\n` +
    `Justification: The as-built site constraints preclude strict compliance without disproportionate cost/disruption. ` +
    `The engineer of record certifies that the proposed condition maintains equivalent safety and performance through compensating measures, ` +
    `and requests a documented exception under the authority having jurisdiction (AHJ). Logged to the project audit trail.`;
}

// ── Cable Test Validator ──────────────────────────────────────────────────────
export interface CableTestRow { id: string; lengthM: number; nextDb: number; rlDb: number; result: string; verdict: CheckStatus }
export interface CableTestResult { rows: CableTestRow[]; total: number; passed: number; failed: number }

export const SAMPLE_TEST_CSV = `CableID,Length_m,NEXT_dB,ReturnLoss_dB,Result
B2-01,42.3,44.1,20.2,PASS
B2-02,58.9,41.7,19.8,PASS
B2-03,93.6,38.9,16.1,PASS
B2-04,101.4,33.2,12.4,FAIL
B2-05,71.2,45.0,21.7,PASS`;

/** Validate pasted test CSV against the 100 m channel + NEXT/RL floors. */
export function validateCableTests(csv: string): CableTestResult {
  const lines = csv.trim().split(/\r?\n/).filter(Boolean);
  const rows: CableTestRow[] = [];
  for (const line of lines) {
    const parts = line.split(',').map((s) => s.trim());
    if (/cableid/i.test(parts[0] ?? '')) continue; // header
    const [id, lenS, nextS, rlS, res] = parts;
    const lengthM = parseFloat(lenS ?? '');
    const nextDb = parseFloat(nextS ?? '');
    const rlDb = parseFloat(rlS ?? '');
    if (!id || Number.isNaN(lengthM)) continue;
    const fail = lengthM > 100 || nextDb < 35 || rlDb < 15 || /fail/i.test(res ?? '');
    rows.push({ id, lengthM, nextDb, rlDb, result: res ?? '', verdict: fail ? 'violation' : 'pass' });
  }
  return {
    rows,
    total: rows.length,
    passed: rows.filter((r) => r.verdict === 'pass').length,
    failed: rows.filter((r) => r.verdict === 'violation').length,
  };
}
